import React from 'react';
import { Button, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { SectionGroupNode } from './form-helpers';
import { FormDefinitionField } from '@/interfaces/api/models';
import FormFieldBinding, { FormFieldWiring } from './FormFieldBinding';
import FormSectionInstance from './FormSectionInstance';
import styles from './FormSection.module.css';

export interface FormSectionProps extends FormFieldWiring {
    sectionGroup: SectionGroupNode;
    formDefinitionFields: FormDefinitionField[];
    form: any;
    onAddInstance: (sectionGroup: SectionGroupNode) => void;
    onRemoveInstance: (sectionGroup: SectionGroupNode) => void;
    disabled?: boolean;
    // Set only on the direct top-level call from `ManageFormModal`, where the Accordion.Control
    // wrapping this section now owns the heading. Recursive `childSections` calls never pass this,
    // so nested repeatable-group headings keep rendering exactly as before.
    suppressHeading?: boolean;
    // Set alongside suppressHeading, for the same reason and on the same call site — see
    // FormSectionInstance's own doc comment for why this must not propagate to childSections either.
    suppressBorder?: boolean;
    // Set alongside suppressHeading/suppressBorder, on the same call site: ManageFormModal renders
    // its own delete action in the Accordion.Control row (next to the chevron) for that case, so
    // FormSectionInstance must not also render one inside the panel underneath it.
    suppressDeleteAction?: boolean;
}

function FormSection({ sectionGroup, formDefinitionFields, form, onAddInstance, onRemoveInstance, disabled, suppressHeading, suppressBorder, suppressDeleteAction, ...wiring }: FormSectionProps) {
    if (sectionGroup.isRepeatable) {
        // Only the first instance of a repeatable group carries the heading (later instances would
        // just repeat it). Passed into FormSectionInstance rather than rendered here as a separate
        // block above it, so it sits in line with the delete action on the same row, at the top of
        // the card, instead of the delete icon floating in its own corner underneath a heading it
        // has no visual relationship to. (Suppressed entirely, along with the delete action, when
        // ManageFormModal's Accordion.Control already owns both — see suppressDeleteAction above.)
        const showHeading = !suppressHeading && !!sectionGroup.heading && sectionGroup.sectionGroup === 0;
        return (
            <div>
                <FormSectionInstance sectionGroup={sectionGroup} formDefinitionFields={formDefinitionFields} form={form} onRemoveInstance={onRemoveInstance} disabled={disabled} suppressBorder={suppressBorder} suppressDeleteAction={suppressDeleteAction} heading={showHeading ? sectionGroup.heading : null} {...wiring} />
                {sectionGroup.childSections.map((child) => (
                    <FormSection key={`${child.sectionID}_${child.sectionGroup}_${child.parentSectionGroup}`} sectionGroup={child} formDefinitionFields={formDefinitionFields} form={form} onAddInstance={onAddInstance} onRemoveInstance={onRemoveInstance} disabled={disabled} {...wiring} />
                ))}
                {sectionGroup.isLast && !disabled && (
                    <div className={styles.addButtonRow}>
                        <Button variant="subtle" leftSection={<IconPlus size={16} />} onClick={() => onAddInstance(sectionGroup)}>
                            Add {sectionGroup.heading}
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            {!suppressHeading && sectionGroup.heading && <Title order={4} className={styles.sectionHeading}>{sectionGroup.heading}</Title>}
            {sectionGroup.formItems.map((item) => {
                const field = formDefinitionFields.find((f) => f.ID === item.FormDefinitionFieldID);
                if (!field) return null;
                // Keying on `form.key(...)` re-seeds the binding's local state whenever the form
                // store is written to from outside a field (initial load, draft restore, upgrade).
                return <FormFieldBinding key={form.key(item.ID)} item={item} field={field} form={form} disabled={disabled} {...wiring} />;
            })}
            {sectionGroup.childSections.map((child) => (
                <FormSection key={`${child.sectionID}_${child.sectionGroup}_${child.parentSectionGroup}`} sectionGroup={child} formDefinitionFields={formDefinitionFields} form={form} onAddInstance={onAddInstance} onRemoveInstance={onRemoveInstance} disabled={disabled} {...wiring} />
            ))}
        </div>
    );
}

export default React.memo(FormSection);
